import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const AUDIENCES = ["Consumers", "Institutions", "Technology partners"];

const ACTIONS = [
  {
    n: "01",
    label: "Request a Demo",
    href: "#contact",
    hint: "See the platform with a specialist",
    tone: "gold",
  },
  {
    n: "02",
    label: "Become a Partner",
    href: "#partner",
    hint: "Join the ecosystem and grow",
    tone: "navy",
  },
  {
    n: "03",
    label: "Register Now",
    href: "#simmarket",
    hint: "Start with smarter financial choices",
    tone: "light",
  },
  {
    n: "04",
    label: "Contact Us",
    href: "#callback",
    hint: "Talk to the SimFinance team",
    tone: "light",
  },
];

function Cta() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="cta"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="cta-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(229,184,11,0.16),transparent_55%),radial-gradient(55%_45%_at_100%_100%,rgba(12,31,27,0.08),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.34]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto grid max-w-8xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16 lg:px-8 lg:py-28">
        <motion.header
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <h2
            id="cta-heading"
            className="font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Ready to transform your
            <span className="mt-1 block">financial services experience?</span>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
            Whether you&apos;re a consumer seeking smarter financial choices, a financial institution
            looking to accelerate growth, or a technology partner building the future of digital
            finance, SimFinance is your trusted ecosystem.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2" aria-label="Who this is for">
            {AUDIENCES.map((item) => (
              <li
                key={item}
                className="border border-navy/10 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70"
              >
                {item}
              </li>
            ))}
          </ul>
        </motion.header>

        <motion.div
          className="relative"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <span className="pointer-events-none absolute -left-2 -top-2 h-10 w-10 border-l border-t border-gold" />
          <span className="pointer-events-none absolute -right-2 -top-2 h-10 w-10 border-r border-t border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -left-2 h-10 w-10 border-b border-l border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold" />

          <div className="relative overflow-hidden border border-navy/10 bg-white">
            <div className="h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
            <div className="grid gap-px bg-navy/10">
              {ACTIONS.map((action, index) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                  className={`group relative flex min-h-[5.5rem] items-center justify-between gap-4 px-5 py-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal sm:px-6 ${
                    action.tone === "gold"
                      ? "bg-gold text-navy hover:bg-[#f0c52a]"
                      : action.tone === "navy"
                        ? "bg-navy text-white hover:bg-[#1a173f]"
                        : "bg-white text-navy hover:bg-[#f3f7f5]"
                  }`}
                  initial={reduce ? false : { opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={
                    reduce ? { duration: 0 } : { duration: 0.4, delay: 0.05 * index, ease: EASE }
                  }
                >
                  <span className="flex min-w-0 items-start gap-4">
                    <span
                      className={`mt-1 shrink-0 font-mono text-[11px] font-semibold tracking-[0.2em] ${
                        action.tone === "gold"
                          ? "text-navy/40"
                          : action.tone === "navy"
                            ? "text-gold"
                            : "text-navy/30"
                      }`}
                    >
                      {action.n}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-lg font-semibold tracking-tight">{action.label}</span>
                      <span
                        className={`mt-1 block text-sm leading-snug ${
                          action.tone === "navy" ? "text-white/55" : "text-navy/50"
                        }`}
                      >
                        {action.hint}
                      </span>
                    </span>
                  </span>
                  <ArrowIcon
                    className={`h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                      action.tone === "navy" ? "text-gold" : "text-navy"
                    }`}
                  />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ArrowIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 12L12 4M12 4H5.5M12 4V10.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Cta;
